import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { Router, ActivatedRoute } from '@angular/router';
import { APP_ROUTES } from '../../enums/appRoutes.enum';
import { Observable, Subject, takeUntil } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { FormStateService, UserProfileService } from 'src/app/core/services';
import { ICurrentPhaseInfo } from 'src/app/core/interfaces';
import { IHeaderButton } from '../../interfaces';
import { LayoutUiService } from '../../services';

@Component({
  standalone: false,
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  pageTitle = 'Home';
  pageSubtitle = '';
  pageButtons: IHeaderButton[] = [];
  isFormValid = false;
  showBackButton = false;

  /**
   * MVP-18 Stage 3 — pipes the cached profile's `currentPhase` snapshot
   * into the global `<app-phase-renewal-banner>` so it can render itself
   * only when renewal is actually due.
   */
  currentPhase$: Observable<ICurrentPhaseInfo | null> = this.userProfileService
    .getProfileObservable()
    .pipe(map(profile => profile?.currentPhase ?? null));
  readonly sidebarOpen$: Observable<boolean> = this.layoutUiService.sidebarOpen$;
  readonly sidebarModal$: Observable<boolean> = this.layoutUiService.sidebarModal$;

  constructor(
    private router: Router,
    private formStateService: FormStateService,
    private activatedRoute: ActivatedRoute,
    private userProfileService: UserProfileService,
    private layoutUiService: LayoutUiService
  ) {}

  /**
   * @description Opens/closes the navigation drawer from the header's menu button.
   * @returns {void} void
   */
  toggleSidebar(): void {
    this.layoutUiService.toggleSidebar();
  }

  /**
   * @description Closes the navigation drawer — scrim click and route change.
   * @returns {void} void
   */
  closeSidebar(): void {
    this.layoutUiService.closeSidebar();
  }

  /**
   * @description Escape closes the drawer, so a keyboard user is never trapped
   * behind the scrim with no pointer to dismiss it (WCAG 2.1.2 No Keyboard Trap).
   * @returns {void} void
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.layoutUiService.isSidebarOpen) {
      this.layoutUiService.closeSidebar();
    }
  }

  ngOnInit(): void {
    this.handleRouteAndNavigation();
    this.subscribeToFormStateChanges();
    this.subscribeToHeaderStateChanges();
  }
  /**
   * @description Handles the route and navigation events
   * @returns {void} void
   */
  private handleRouteAndNavigation(): void {
    this.updateHeader(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        this.updateHeader(event.url);
        this.layoutUiService.closeSidebar();
      });
  }
  /**
   * @description Subscribes to form state changes
   * @returns {void} void
   */
  private subscribeToFormStateChanges(): void {
    this.formStateService
      .getFormState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.isFormValid = state.isValid;
        this.updateButtonState();
      });
  }

  /**
   * @description Subscribes to header state changes
   * @returns {void} void
   */
  private subscribeToHeaderStateChanges(): void {
    this.formStateService
      .getHeaderState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (state) {
          this.pageTitle = state.title;
          this.pageSubtitle = state.subtitle || '';
        }
      });
  }

  /**
   * @description Updates the button state based on form validation and user role
   * @returns {void} void
   */
  private updateButtonState(): void {
    if (this.router.url.includes(APP_ROUTES.NEW_TEST)) {
      const runTestButton = this.pageButtons.find(button => button.id === 'run-test');
      if (runTestButton) {
        runTestButton.disabled = !this.isFormValid || this.userProfileService.isViewer();
      }
    }
  }

  /**
   * @description Updates the header title and buttons dynamically based on the route.
   * @param {string} url - The current route URL.
   */
  private updateHeader(url: string): void {
    this.showBackButton = false;
    this.pageSubtitle = '';
    if (url.includes(APP_ROUTES.VIEW_TEST)) {
      this.pageTitle = 'shared.components.header.view_test';
      this.showBackButton = true;
      this.pageButtons = [];
    } else if (url.startsWith(`/${APP_ROUTES.ADMIN}`)) {
      this.pageTitle = this.adminPageTitle(url);
      this.pageButtons = [];
    } else if (url.includes(APP_ROUTES.HOME)) {
      this.pageTitle = 'shared.components.header.home';
      this.pageButtons = [
        {
          id: 'new-test',
          label: 'shared.actions.buttons.create_test',
          action: this.createTest.bind(this),
          icon: 'assets/icons/add.svg',
          buttonType: 'primary',
        },
      ];
    } else if (url.includes(APP_ROUTES.NEW_TEST)) {
      this.pageTitle = 'shared.components.header.new_test';
      this.showBackButton = true;
      this.pageButtons = [
        {
          id: 'save-test',
          label: 'shared.actions.buttons.save',
          action: this.saveTest.bind(this),
          icon: 'assets/icons/save.svg',
          buttonType: 'secondary',
        },
        {
          id: 'run-test',
          label: 'shared.actions.buttons.calculate',
          action: this.runTest.bind(this),
          icon: 'assets/icons/check.svg',
          disabled: !this.isFormValid || this.userProfileService.isViewer(),
          buttonType: 'primary',
        },
      ];
      this.updateButtonState();
    } else if (url.includes(`${APP_ROUTES.TESTS}/${APP_ROUTES.CLINICAL_REPORT}`)) {
      // Handle clinical report routes first (more specific)
      this.pageTitle = 'shared.components.header.clinical_report';
      this.showBackButton = true;
      this.pageButtons = [
        {
          id: 'update-test',
          label: 'shared.actions.buttons.edit',
          action: this.updateTest.bind(this),
          icon: 'assets/icons/edit.svg',
          buttonType: 'secondary',
        },
        {
          id: 'print-report',
          label: 'shared.actions.buttons.print',
          action: this.printReport.bind(this),
          icon: 'assets/icons/print.svg',
          buttonType: 'secondary',
        },
      ];
    } else if (url.includes(APP_ROUTES.TESTS_LIST) || url === `/${APP_ROUTES.TESTS}` || url === APP_ROUTES.TESTS) {
      // Handle both /tests/list and intermediate /tests URL
      this.pageTitle = 'shared.components.header.test_results';
      this.pageButtons = [
        {
          id: 'new-test',
          label: 'shared.actions.buttons.create_test',
          action: this.createTest.bind(this),
          icon: 'assets/icons/add.svg',
          buttonType: 'primary',
        },
      ];
    } else if (url.includes(APP_ROUTES.SETTINGS)) {
      this.pageTitle = 'shared.components.header.settings';
      this.pageButtons = [
        {
          id: 'new-test',
          label: 'shared.actions.buttons.create_test',
          action: this.createTest.bind(this),
          icon: 'assets/icons/add.svg',
          buttonType: 'primary',
        },
      ];
    }
  }

  /**
   * @description Resolves the header title for a super-user route from the same
   * translation key the page itself uses.
   * @param {string} url - The current route URL.
   * @returns {string} translation key of the page title
   */
  private adminPageTitle(url: string): string {
    const section = url.replace(`/${APP_ROUTES.ADMIN}/`, '').split(/[/?]/)[0];
    const titles: Record<string, string> = {
      tests: 'modules.superUser.tests.title',
      customers: 'modules.superUser.users.title',
      'customer-requests': 'modules.superUser.salesContactRequests.title',
      export: 'modules.superUser.export.title',
      'api-clients': 'modules.superUser.apiClients.title',
      settings: 'shared.components.header.settings',
    };
    return titles[section] ?? 'shared.components.header.admin';
  }

  /**
   * @description Handles creating a new test.
   */
  private createTest(): void {
    this.router.navigate([APP_ROUTES.NEW_TEST]);
  }

  /**
   * @description Handles running a test.
   * @returns {void} void
   */
  private runTest(): void {
    if (this.isFormValid && !this.userProfileService.isViewer()) {
      this.formStateService.triggerAction('calculate');
    }
  }

  /**
   * @description Handles saving a test.
   */
  private saveTest(): void {
    this.formStateService.triggerAction('save');
  }

  /**
   * @description Handles updating a test - navigates to score form with requestId
   */
  private updateTest(): void {
    // Use Angular's reactive approach to get the current route parameter
    const requestId = this.getCurrentRouteParam('requestId');

    if (requestId) {
      this.router.navigate([`${APP_ROUTES.NEW_TEST}/${requestId}`]);
    } else {
      // Fallback to new test if no requestId is available
      this.router.navigate([APP_ROUTES.NEW_TEST]);
    }
  }

  /**
   * @description Gets a route parameter from the current route tree using Angular's best practices
   * @param {string} paramName - The name of the parameter to retrieve
   * @returns {string | null} The parameter value if found, null otherwise
   */
  private getCurrentRouteParam(paramName: string): string | null {
    // Use Angular's proper route traversal mechanism
    let route: ActivatedRoute | null = this.activatedRoute;

    // Check current route and all child routes
    while (route) {
      if (route.snapshot.params[paramName]) {
        return route.snapshot.params[paramName];
      }
      route = route.firstChild;
    }

    // If not found in children, check parent route
    route = this.activatedRoute.parent;
    while (route) {
      if (route.snapshot.params[paramName]) {
        return route.snapshot.params[paramName];
      }
      route = route.parent;
    }

    return null;
  }

  /**
   * @description Handles printing the report.
   * @returns {void} void
   */
  private printReport(): void {
    this.formStateService.triggerAction('print');
  }

  /**
   * @description Navigates back to the test results list. when the back button is shown on the super-user read-only draft view
   * (`/admin/tests/view/:id`), return to the admin tests list so the super user stays in the Admin perspective instead of dropping into the clinical list.
   */
  goBack(): void {
    if (this.router.url.startsWith(`/${APP_ROUTES.ADMIN}/`)) {
      this.router.navigate([`/${APP_ROUTES.ADMIN_TESTS}`]);
      return;
    }
    this.router.navigate([APP_ROUTES.TESTS_LIST]);
  }

  /**
   * @description Updates the page subtitle
   * @param {string} subtitle - The subtitle to display
   */
  updateSubtitle(subtitle: string): void {
    this.pageSubtitle = subtitle;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
